import type { DocPoint } from './features'
import type { PhotoRef } from './imageIndex'
import { evictToBudget } from './playback'

export type LoadResult =
  | { kind: 'image'; image: HTMLCanvasElement }
  | { kind: 'missing'; path: string }
  | { kind: 'no-path' }
  | { kind: 'error'; error: unknown }
  | { kind: 'skipped' }

export type FrameLoaderOptions = {
  file: Blob
  points: DocPoint[]
  read: (file: Blob, photo: PhotoRef) => Promise<Blob>
  decode: (blob: Blob) => Promise<HTMLCanvasElement>
  rank: (featureIndex: number) => number
  concurrency: number
  budget: number
  maxFrames: number
}

type Job = {
  featureIndex: number
  priority: boolean
  resolve: (result: LoadResult) => void
}

const SKIPPED: LoadResult = { kind: 'skipped' }

export class FrameLoader {
  private readonly cache = new Map<number, { image: HTMLCanvasElement; bytes: number }>()
  private readonly inflight = new Map<number, Promise<LoadResult>>()
  private queue: Job[] = []
  private active = 0
  private disposed = false

  constructor(private readonly options: FrameLoaderOptions) {}

  get pending(): number {
    return this.queue.length + this.active
  }

  load(featureIndex: number, priority = false): Promise<LoadResult> {
    if (this.disposed) return Promise.resolve(SKIPPED)
    const hit = this.cache.get(featureIndex)
    if (hit) return Promise.resolve({ kind: 'image', image: hit.image })
    const point = this.options.points[featureIndex]
    if (!point) return Promise.resolve(SKIPPED)
    if (!point.image) {
      return Promise.resolve(point.imagePath ? { kind: 'missing', path: point.imagePath } : { kind: 'no-path' })
    }
    const running = this.inflight.get(featureIndex)
    if (running) {
      if (priority) this.promote(featureIndex)
      return running
    }
    const promise = new Promise<LoadResult>(resolve => {
      const job = { featureIndex, priority, resolve }
      if (priority) this.queue.unshift(job)
      else this.queue.push(job)
    })
    this.inflight.set(featureIndex, promise)
    this.pump()
    return promise
  }

  prune() {
    const keep: Job[] = []
    for (const job of this.queue) {
      if (job.priority || Number.isFinite(this.options.rank(job.featureIndex))) keep.push(job)
      else this.finish(job, SKIPPED)
    }
    this.queue = keep
  }

  dispose() {
    this.disposed = true
    const queued = this.queue
    this.queue = []
    queued.forEach(job => this.finish(job, SKIPPED))
    this.cache.clear()
  }

  private promote(featureIndex: number) {
    const index = this.queue.findIndex(job => job.featureIndex === featureIndex)
    if (index < 0) return
    const [job] = this.queue.splice(index, 1)
    job.priority = true
    this.queue.unshift(job)
  }

  private pump() {
    while (!this.disposed && this.active < this.options.concurrency && this.queue.length) {
      const job = this.queue.shift()
      if (job) void this.run(job)
    }
  }

  private finish(job: Job, result: LoadResult) {
    this.inflight.delete(job.featureIndex)
    job.resolve(result)
  }

  private async run(job: Job) {
    this.active += 1
    let result: LoadResult = SKIPPED
    try {
      if (!job.priority && !Number.isFinite(this.options.rank(job.featureIndex))) return
      const photo = this.options.points[job.featureIndex]?.image
      if (!photo) return
      const blob = await this.options.read(this.options.file, photo)
      if (this.disposed) return
      const image = await this.options.decode(blob)
      if (this.disposed) return
      this.remember(job.featureIndex, image)
      result = { kind: 'image', image }
    } catch (error) {
      if (!this.disposed) result = { kind: 'error', error }
    } finally {
      this.active -= 1
      this.finish(job, result)
      this.pump()
    }
  }

  private remember(featureIndex: number, image: HTMLCanvasElement) {
    this.cache.set(featureIndex, { image, bytes: image.width * image.height * 4 })
    const kept = new Set(
      evictToBudget(
        [...this.cache.entries()].map(([key, entry]) => ({ key, bytes: entry.bytes })),
        this.options.rank,
        this.options.budget,
        this.options.maxFrames
      ).map(entry => entry.key)
    )
    for (const key of this.cache.keys()) {
      if (!kept.has(key)) this.cache.delete(key)
    }
  }
}
